<template>
  <div class="crud_form_wrapper payout_detail">
    <div class="form_title_wrapper d-flex align-center justify-space-between">
      <h4>{{ $t("bookingPayouts.show") }}</h4>
      <v-btn @click="$router.go(-1)" style="color: var(--main_theme_clr)">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>

    <div class="single_step_form_content_wrapper" v-if="data">
      <div class="summary_bar">
        <div>
          <span class="muted">{{ $t("bookingPayouts.id") }}</span>
          <strong>#{{ data.id }}</strong>
        </div>
        <div>
          <span class="muted">{{ $t("bookingPayouts.booking_id") }}</span>
          <strong>{{ data.booking_number || data.booking_id || "-" }}</strong>
        </div>
        <div>
          <span class="muted">{{ $t("payouts.fields.user") }}</span>
          <strong>{{ userName }}</strong>
        </div>
        <div>
          <span class="muted">{{ $t("bookingPayouts.part") }}</span>
          <strong>{{ partLabel(data.part) }}</strong>
        </div>
        <span class="status_chip" :class="statusClass(data.status)">
          {{ statusLabel(data.status) }}
        </span>
      </div>

      <h5 class="section_title">{{ $t("bookingPayouts.amounts") }}</h5>
      <div class="amount_grid">
        <div class="amount_card" v-for="item in amountCards" :key="item.label">
          <span>{{ item.label }}</span>
          <strong>{{ item.value }}</strong>
        </div>
      </div>

      <h5 class="section_title">{{ $t("bookingPayouts.dates") }}</h5>
      <div class="dates_row">
        <div v-for="item in dateFacts" :key="item.label">
          <span class="muted">{{ item.label }}</span>
          <p>{{ item.value }}</p>
        </div>
      </div>

      <div v-if="reasonText !== '-'">
        <h5 class="section_title">{{ $t("bookingPayouts.applied_reason") }}</h5>
        <p>{{ reasonText }}</p>
      </div>

      <h5 class="section_title">{{ $t("bookingPayouts.releases") }}</h5>
      <v-data-table
        class="releases_table"
        :headers="siblingHeaders"
        :items="siblings"
        hide-default-footer
        :items-per-page="20"
      >
        <template v-slot:no-data>{{ $t("TABLES.noData") }}</template>
        <template v-slot:[`item.user`]="{ item }">
          {{ item.user && item.user.name ? item.user.name : "-" }}
        </template>
        <template v-slot:[`item.part`]="{ item }">
          {{ partLabel(item.part) }}
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <span class="status_chip" :class="statusClass(item.status)">
            {{ statusLabel(item.status) }}
          </span>
        </template>
        <template v-slot:[`item.applied_reason`]="{ item }">
          {{ reasonLabel(item.applied_reason) }}
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <button class="btn_show" @click="openRow(item)">
            <i class="fal fa-eye"></i>
          </button>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import payoutLabels from "@/utils/payoutLabels";

export default {
  name: "ShowBookingPayout",
  data() {
    return {
      data: null,
      siblings: [],
      siblingHeaders: [
        { text: this.$t("bookingPayouts.id"), value: "id", align: "center", sortable: false },
        { text: this.$t("bookingPayouts.part"), value: "part", align: "center", sortable: false },
        { text: this.$t("bookingPayouts.amount"), value: "amount", align: "center", sortable: false },
        { text: this.$t("bookingPayouts.transferred_amount"), value: "transferred_amount", align: "center", sortable: false },
        { text: this.$t("bookingPayouts.wallet_applied_amount"), value: "wallet_applied_amount", align: "center", sortable: false },
        { text: this.$t("bookingPayouts.status"), value: "status", align: "center", sortable: false },
        { text: this.$t("bookingPayouts.applied_reason"), value: "applied_reason", align: "center", sortable: false },
        { text: this.$t("bookingPayouts.released_at"), value: "released_at", align: "center", sortable: false },
        { text: this.$t("TABLES.Admins.actions"), value: "actions", align: "center", sortable: false },
      ],
    };
  },
  computed: {
    userName() {
      if (this.data?.user?.name) return this.data.user.name;
      return this.data?.user_id ? `#${this.data.user_id}` : "-";
    },
    reasonText() {
      return this.reasonLabel(this.data?.applied_reason);
    },
    amountCards() {
      if (!this.data) return [];
      const cards = this.present([
        ["amount", "bookingPayouts.amount"],
        ["transferred_amount", "bookingPayouts.transferred_amount"],
        ["wallet_applied_amount", "bookingPayouts.wallet_applied_amount"],
        ["order_total", "bookingPayouts.order_total"],
        ["commission_total", "bookingPayouts.commission_total"],
        ["net", "bookingPayouts.net"],
        ["cash_amount", "bookingPayouts.cash_amount"],
      ]);
      const hold = this.data.hold_percent;
      if (hold !== null && hold !== undefined && hold !== "") {
        cards.push({
          label: this.$t("bookingPayouts.hold_percent"),
          value: this.formatPercent(hold),
        });
      }
      return cards;
    },
    dateFacts() {
      if (!this.data) return [];
      return this.present([
        ["available_at", "bookingPayouts.available_at"],
        ["auto_release_at", "bookingPayouts.auto_release_at"],
        ["finished_at", "bookingPayouts.finished_at"],
        ["released_at", "bookingPayouts.released_at"],
      ]);
    },
  },
  watch: {
    "$route.params.id"() {
      this.load();
    },
  },
  methods: {
    text(value) {
      return value === null || value === undefined || value === "" ? "-" : String(value);
    },
    formatPercent(value) {
      const raw = String(value).trim();
      if (!raw || raw === "-") return "-";
      return raw.endsWith("%") ? raw : `${raw}%`;
    },
    present(pairs) {
      return pairs
        .filter(([key]) => {
          const value = this.data[key];
          return value !== null && value !== undefined && value !== "";
        })
        .map(([key, label]) => ({
          label: this.$t(label),
          value: this.text(this.data[key]),
        }));
    },
    partLabel(value) {
      return payoutLabels.part(this, value);
    },
    statusLabel(status) {
      return payoutLabels.status(this, status);
    },
    reasonLabel(value) {
      return payoutLabels.reason(this, value);
    },
    statusClass(status) {
      return {
        held: status === "held_complaint" || status === "failed",
        wallet: status === "applied_to_wallet" || status === "processing" || status === "scheduled",
        done: status === "transferred",
      };
    },
    openRow(item) {
      if (String(item.id) === String(this.$route.params.id)) return;
      this.$router.push({ path: `/booking-payouts/show/${item.id}` });
    },
    async load() {
      try {
        const res = await this.$axios.get(`payouts/${this.$route.params.id}`);
        const payload = res.data.data || {};
        this.data = payload.payout || payload.Payout || payload;
        await this.loadSiblings();
      } catch (error) {
        this.$message.error(error.response?.data?.message);
      }
    },
    async loadSiblings() {
      if (!this.data?.booking_id) {
        this.siblings = this.data ? [this.data] : [];
        return;
      }
      try {
        const res = await this.$axios.get("payouts", {
          params: {
            purpose: "booking_release",
            booking_id: this.data.booking_id,
            limit: 20,
          },
        });
        const payload = res.data.data;
        const rows = Array.isArray(payload) ? payload : payload?.data || [];
        this.siblings = rows.length ? rows : [this.data];
      } catch (error) {
        this.siblings = [this.data];
      }
    },
  },
  created() {
    this.load();
  },
};
</script>

<style scoped lang="scss">
.payout_detail {
  .summary_bar,
  .dates_row {
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
    align-items: center;
    margin-bottom: 24px;
  }

  .muted {
    display: block;
    color: #7a7a7a;
    font-size: 13px;
    margin-bottom: 4px;
  }

  .section_title {
    margin: 8px 0 16px;
    font-size: 16px;
    font-weight: 700;
  }

  .amount_grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 12px;
    margin-bottom: 28px;
  }

  .amount_card {
    border: 1px solid #eee;
    border-radius: 10px;
    padding: 14px 16px;
    background: #fafafa;

    span {
      display: block;
      color: #7a7a7a;
      font-size: 13px;
      margin-bottom: 6px;
    }

    strong {
      font-size: 18px;
    }
  }

  .status_chip {
    display: inline-block;
    padding: 6px 12px;
    border-radius: 999px;
    background: #eee;
    font-weight: 600;
    font-size: 13px;

    &.held {
      background: #fdecea;
      color: #c62828;
    }

    &.wallet {
      background: #fff4e5;
      color: #ef6c00;
    }

    &.done {
      background: #e8f5e9;
      color: #2e7d32;
    }
  }

  .releases_table {
    border: 1px solid #eee;
    border-radius: 10px;
  }
}
</style>
